import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { LeaderboardContext } from "../contexts/leaderboard-context";
import { SiteVarsContext } from "../contexts/sitevars-context";
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";
import { getConfirmedTeamResource } from "../services/transfers.service";
import PremPlayersContext from "../contexts/premplayers-context";
import PremPlayersProvider from "../providers/premplayers-provider";
import { PageLayout } from "../components/page-layout";

const ConfirmedTeamsContent = () => {
  const { leaderboardData, isLoadingLeaderboardData } = useContext(LeaderboardContext);
  const { siteVarsData, isLoadingSiteVarsData } = useContext(SiteVarsContext);
  const { getPlayerPositionById, getTeamById } = useSiteVarsFunctions();
  const { premPlayersData, isLoadingPremPlayersData } = useContext(PremPlayersContext);

  const [confirmedTeams, setConfirmedTeams] = useState([]);
  const [isLoadingConfirmedTeams, setIsLoadingConfirmedTeams] = useState(true);

  useEffect(() => {
    const fetchAllConfirmedTeams = async () => {
      if (leaderboardData.length > 0 && siteVarsData && siteVarsData.game_vars && premPlayersData.length > 0) {
        const currentWindow = siteVarsData.game_vars.currentWindow;
        const teamsData = [];

        for (const club of leaderboardData) {
          const { data, error } = await getConfirmedTeamResource(club.clubId, currentWindow);
          if (data && data.team) {
            const playersWithDetails = data.team.map(playerId => {
              const fullPlayerData = premPlayersData.find(p => p.playerId === playerId);
              return fullPlayerData ? {
                ...fullPlayerData,
                positionName: getPlayerPositionById(fullPlayerData.positionId).name_short,
                teamName: getTeamById(fullPlayerData.teamId).name_short
              } : null;
            }).filter(Boolean); // Filter out any nulls if player data isn't found

            playersWithDetails.sort((a, b) => {
              if (a.positionId !== b.positionId) {
                return a.positionId - b.positionId;
              }
              return a.lastName.localeCompare(b.lastName);
            });

            teamsData.push({ clubName: club.clubName, team: playersWithDetails });
          } else if (error) {
            console.error(`Error fetching confirmed team for club ${club.clubName}:`, error);
            teamsData.push({ clubName: club.clubName, team: [], error: true });
          } else {
            teamsData.push({ clubName: club.clubName, team: [] });
          }
        }
        setConfirmedTeams(teamsData);
        setIsLoadingConfirmedTeams(false);
      }
    };

    if (!isLoadingLeaderboardData && !isLoadingSiteVarsData && !isLoadingPremPlayersData) {
      fetchAllConfirmedTeams();
    }
  }, [leaderboardData, siteVarsData, premPlayersData, isLoadingLeaderboardData, isLoadingSiteVarsData, isLoadingPremPlayersData, getPlayerPositionById, getTeamById]);

  if (isLoadingLeaderboardData || isLoadingSiteVarsData || isLoadingPremPlayersData || isLoadingConfirmedTeams) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Confirmed Teams
      </Typography>
      {confirmedTeams.map((clubData, index) => (
        <Paper key={index} sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            {clubData.clubName}
          </Typography>
          {clubData.team.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Player</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Team</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clubData.team.map((player, playerIndex) => (
                    <TableRow key={playerIndex}>
                      <TableCell>{player.lastName}</TableCell>
                      <TableCell>{player.positionName}</TableCell>
                      <TableCell>{player.teamName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
              This club has not confirmed their team.
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
};

export const ConfirmedTeamsPage = () => {
  return (
    <PageLayout>
      <PremPlayersProvider>
        <ConfirmedTeamsContent />
      </PremPlayersProvider>
    </PageLayout>
  );
};